/**
 * What the legacy loader (loadSavedProgress) treats as "this learner has saved
 * progress", from their enrollment row (job #890).
 *
 * The gate used to be `last_completed_round_index !== null`. A row whose cursor
 * (last_completed_lego_id) is set but whose round index was never written — an
 * admin or cursor-repair write, a fixture, a position set before the first
 * played round — read as a brand-new learner, so the slow-link fallback to the
 * legacy loader started them at S0001L01 while the instant path, which resolves
 * from the lego id, resumed them correctly. The lego id IS the position; the
 * round index is a derived hint and may be null.
 */
export interface EnrollmentRowLike {
  last_completed_lego_id?: string | null
  last_completed_round_index?: number | null
  total_practice_minutes?: number
  current_cycle_index?: number | null
  last_practiced_at?: Date | null
  infplay_round_index?: number | null
}

export function savedProgressFromEnrollment(enrollment: EnrollmentRowLike | null | undefined) {
  if (!enrollment) return null
  const hasPosition = enrollment.last_completed_round_index != null
    || !!enrollment.last_completed_lego_id
    || (enrollment.total_practice_minutes ?? 0) > 0
  if (!hasPosition) return null
  return {
    lastCompletedLegoId: enrollment.last_completed_lego_id ?? null,
    lastCompletedRoundIndex: enrollment.last_completed_round_index ?? null,
    totalPracticeMinutes: enrollment.total_practice_minutes ?? 0,
    currentCycleIndex: enrollment.current_cycle_index ?? 0,
    lastPracticedAt: enrollment.last_practiced_at ?? null,
    infplayRoundIndex: enrollment.infplay_round_index ?? 0,
  }
}

/**
 * The round to resume on in a loaded script: the saved round index, or — when
 * the row has only a cursor (job #890) — the round that holds that LEGO.
 * Null = nothing to resume from (no position, or the LEGO is not in these rounds).
 */
export function resolveSavedRoundIndex(
  saved: { lastCompletedRoundIndex: number | null; lastCompletedLegoId: string | null } | null | undefined,
  rounds: ReadonlyArray<{ legoId?: string | null }>,
): number | null {
  if (!saved) return null
  if (saved.lastCompletedRoundIndex != null) return saved.lastCompletedRoundIndex
  if (!saved.lastCompletedLegoId) return null
  const i = rounds.findIndex((r) => r?.legoId === saved.lastCompletedLegoId)
  return i >= 0 ? i : null
}
